import { type AutocompleteProps } from '@mui/material';

import { isDefined, isTruthy } from '../../utils/types.js';

export const API_AUTOCOMPLETE_FIND_ALL_LIMIT = 100;

export function getApiAutocompleteErrorMessage(props: {
  errorMessage?: string;
  isFindAllError?: boolean;
  isFindOneError?: boolean;
}) {
  const { errorMessage, isFindAllError, isFindOneError } = props;
  if (errorMessage) {
    return errorMessage;
  }
  if (isFindAllError) {
    return 'Failed to load options. Please try again later.';
  }
  if (isFindOneError) {
    return 'Failed to load the selected option. Please try again later.';
  }
  return undefined;
}

type GetAutocompletePlaceholderProps<T> = Pick<
  AutocompleteProps<T, boolean, false, boolean>,
  'options' | 'freeSolo' | 'value'
> & { placeholder?: string };

/**
 * Returns the placeholder text for an autocomplete input.
 *
 * Rules:
 * - If `value` is an array with at least one selected item, no placeholder is shown.
 * - If a custom `placeholder` is provided, it takes precedence.
 * - Otherwise:
 *    - If `isFreeSolo` is false:
 *        - Show "Type to search..."
 *    - If `isFreeSolo` is true:
 *        - Show "Type to add or search..." when options exist.
 *        - Show "Type to add..." when no options exist.
 */
export function getAutocompletePlaceholder<T>(props: GetAutocompletePlaceholderProps<T>) {
  const { options, freeSolo, value, placeholder } = props;

  const hasSelectedValues = Array.isArray(value) && value.length > 0;

  if (hasSelectedValues) {
    return undefined;
  }

  if (isDefined(placeholder)) {
    return placeholder;
  }

  if (!isTruthy(freeSolo)) {
    return 'Type to search...';
  }

  return isTruthy(options?.length) ? 'Type to add or search...' : 'Type to add...';
}
